import { PhotoAnalysis, ImageSource, PhotoMediumType, SubjectIdentity } from '../types';
import { api, KinveraApiError } from '../lib/api';
import type { DetectedFace } from './faceDetection';
import { detectFacesMultiScale } from './faceDetection';
import { SOURCE_TYPE_LABELS } from './quickActions';

const jobIdsByKey = new Map<string, string>();

/** @deprecated Token attachment is handled by setAuthTokenProvider in AuthContext. */
export function setApiAuthToken(_token: string | null) {
  /* no-op — kept for call-site compatibility */
}

export function getJobIdForImage(key: string) {
  return jobIdsByKey.get(key);
}

export const MEDIUM_LABELS: Record<PhotoMediumType, string> = {
  'digital-photo': 'Native Digital Photograph',
  'silver-gelatin-bw': 'Silver Gelatin Black & White',
  'sepia-albumen': 'Warm Sepia / Albumen Print',
  'kodachrome-color': 'Vintage Kodachrome Color',
  'polaroid-instant': 'Polaroid / Instant Color Print',
  'aged-c41-color': 'Aged C-41 / Faded Color Print',
  'hand-tinted-portrait': 'Hand-Tinted Monochrome Portrait',
  'cyanotype-monochrome': 'Cyanotype Blue Monochrome',
  'platinum-palladium': 'Platinum / Palladium Fine Art Print',
  'daguerreotype-tintype': 'Tintype / Daguerreotype Metal Plate',
};

export { SOURCE_TYPE_LABELS };
function withCoverageStats(
  analysis: PhotoAnalysis,
  subjects: SubjectIdentity[]
): PhotoAnalysis {
  const mappedFaceCount = subjects.filter(
    (s) => s.faceBounds && s.faceBoundsSource !== 'legacy'
  ).length;
  const visiblePersonCount = subjects.length;
  return {
    ...analysis,
    subjects,
    visiblePersonCount,
    mappedFaceCount,
    unmappedSubjectCount: Math.max(0, visiblePersonCount - mappedFaceCount),
  };
}

/**
 * Attach MediaPipe / user face boxes as Subject Map geometry.
 * Gemini subjects without a matching detector index remain as text-only cards.
 * Detector count is NOT used to drop people Gemini identified.
 */
export function mergeDetectorBoundsIntoAnalysis(
  analysis: PhotoAnalysis,
  detectedFaces: DetectedFace[]
): PhotoAnalysis {
  const byIndex = new Map(detectedFaces.map((f) => [f.index, f]));
  const geminiSubjects = analysis.subjects || [];
  const geminiByIndex = new Map(geminiSubjects.map((s) => [s.index, s]));

  const mappedFaceCount = detectedFaces.length;
  const geminiCount = geminiSubjects.length;

  if (mappedFaceCount > 0 || geminiCount > 0) {
    console.info(
      `[Kinvera] subject coverage: visible(gemini)=${geminiCount} mapped(detector)=${mappedFaceCount}`
    );
  }

  // Prefer Gemini's people list as the subject set; attach detector boxes by Person #.
  // Also keep detector-only faces that Gemini omitted (user can fill identity later).
  if (detectedFaces.length === 0) {
    return withCoverageStats(
      analysis,
      geminiSubjects.map((s) => ({
        ...s,
        faceBounds: s.faceBoundsSource === 'user' ? s.faceBounds : undefined,
        faceBoundsSource: s.faceBoundsSource === 'user' ? 'user' : s.faceBounds ? 'legacy' : undefined,
      }))
    );
  }

  const merged: SubjectIdentity[] = [];
  const usedDetector = new Set<number>();

  // 1) Gemini subjects first (authoritative WHO count)
  for (const gem of geminiSubjects) {
    const face = byIndex.get(gem.index);
    const { faceBounds: priorBounds, faceBoundsSource: priorSource, ...rest } = gem;

    // User-supplied placements always win over detector boxes
    if (priorSource === 'user' && priorBounds) {
      if (face) usedDetector.add(face.index);
      merged.push({
        ...rest,
        index: gem.index,
        faceBounds: priorBounds,
        faceBoundsSource: 'user',
      });
      continue;
    }

    if (face) {
      usedDetector.add(face.index);
      merged.push({
        ...rest,
        index: gem.index,
        faceBounds: face.faceBounds,
        faceBoundsSource: 'detector',
      });
    } else {
      merged.push({
        ...rest,
        faceBounds: undefined,
        faceBoundsSource: undefined,
      });
    }
  }

  // 2) Detector faces Gemini did not cover — keep as mapped placeholders
  for (const face of detectedFaces) {
    if (usedDetector.has(face.index) || geminiByIndex.has(face.index)) continue;
    // Avoid index collisions with Gemini-only extras that used different numbers
    const taken = new Set(merged.map((s) => s.index));
    let index = face.index;
    if (taken.has(index)) {
      index = Math.max(...taken, 0) + 1;
    }
    merged.push({
      index,
      perceivedGender: 'unspecified',
      apparentAge: 'Unknown',
      facialStructure: 'Preserve natural facial features and bone structure.',
      hairAndStyling: 'Period hairstyle.',
      genderSafeguards: 'Strictly preserve original facial likeness without artificial gender bias.',
      facialSoftnessGuidance: 'Maintain smooth analog optical softness.',
      visualLocator: `Mapped face (no AI description yet)`,
      visualDescription: `Detector-mapped face; identity details unspecified — please review.`,
      faceBounds: face.faceBounds,
      faceBoundsSource: 'detector',
    });
  }

  merged.sort((a, b) => a.index - b.index);
  return withCoverageStats(analysis, merged);
}

/**
 * Analyze uses the preview; original is stored server-side unchanged.
 * Optional subjectMap is ANALYSIS-ONLY (numbered markers) and never used for restoration.
 */
export const analyzePhotograph = async (
  preview: ImageSource,
  original: ImageSource,
  options?: {
    cacheKey?: string;
    subjectMap?: ImageSource | null;
    detectedFaces?: DetectedFace[];
    /** Preview data URL for optional multi-scale refine */
    previewUrl?: string;
  }
): Promise<{ analysis: PhotoAnalysis; jobId?: string; warning?: string | null }> => {
  const key = options?.cacheKey || original.fileName || original.dataUrl.slice(0, 64);
  let detectedFaces = options?.detectedFaces || [];
  try {
    const res = await api.analyze({
      original: {
        base64Data: original.base64Data,
        mimeType: original.mimeType,
        fileName: original.fileName,
      },
      preview: {
        base64Data: preview.base64Data,
        mimeType: preview.mimeType,
        fileName: preview.fileName,
      },
      subjectMap: options?.subjectMap
        ? {
            base64Data: options.subjectMap.base64Data,
            mimeType: options.subjectMap.mimeType,
            fileName: options.subjectMap.fileName,
          }
        : undefined,
      detectedFaces: detectedFaces.map((f) => ({
        index: f.index,
        faceBounds: f.faceBounds,
        confidence: f.confidence,
      })),
      captureHints: original.captureHints || null,
      fileName: original.fileName,
    });

    if (!res.analysis || typeof res.analysis !== 'object') {
      throw new KinveraApiError('Analysis response did not include analysis data.', {
        code: 'ANALYZE_EMPTY',
        retryable: true,
      });
    }

    if (res.jobId) {
      jobIdsByKey.set(key, res.jobId);
    }
    let analysis = res.analysis as PhotoAnalysis;
    analysis.photoTypeLabel =
      analysis.photoTypeLabel || MEDIUM_LABELS[analysis.photoType] || analysis.photoType;
    if (analysis.sourceType) {
      analysis.sourceTypeLabel =
        analysis.sourceTypeLabel ||
        SOURCE_TYPE_LABELS[analysis.sourceType] ||
        analysis.sourceType;
    }

    const geminiCount = Array.isArray(analysis.subjects) ? analysis.subjects.length : 0;

    // If visual analysis found more people than dual-pass localization, try multi-scale crops
    if (
      geminiCount > detectedFaces.length &&
      (options?.previewUrl || preview.dataUrl)
    ) {
      try {
        const refined = await detectFacesMultiScale(
          options?.previewUrl || preview.dataUrl,
          detectedFaces
        );
        if (refined.length > detectedFaces.length) {
          console.info(
            `[Kinvera] multi-scale refine: ${detectedFaces.length} → ${refined.length} mapped faces`
          );
          detectedFaces = refined;
        }
      } catch (err) {
        console.warn('Multi-scale face refine skipped:', err);
      }
    }

    analysis = mergeDetectorBoundsIntoAnalysis(analysis, detectedFaces);
    return { analysis, jobId: res.jobId, warning: res.warning };
  } catch (error: unknown) {
    if (error instanceof KinveraApiError) {
      if (error.status === 402 || error.code === 'INSUFFICIENT_CREDITS') {
        throw new KinveraApiError(
          error.message ||
            'This restoration requires 1 Kinvera credit. You currently do not have enough credits to begin the restoration.',
          error
        );
      }
      throw error;
    }
    const err = error as Error;
    throw new KinveraApiError(err.message || String(error), { code: 'ANALYZE_FAILED', retryable: true });
  }
};

/**
 * Restore uses the highest-quality working source (full original unless crop/rotate changed it).
 */
export const restorePhotograph = async (
  restoreSource: ImageSource,
  analysis: PhotoAnalysis,
  instructionOverride?: string,
  referenceImage?: ImageSource,
  damageMask?: ImageSource,
  maskSummary?: string | null,
  options?: { isTweak?: boolean; jobId?: string | null; cacheKey?: string }
): Promise<{ imageUrl: string; commentary: string; jobId?: string; warning?: string | null }> => {
  const key = options?.cacheKey || restoreSource.fileName || restoreSource.dataUrl.slice(0, 64);
  const jobId = options?.jobId || jobIdsByKey.get(key);

  try {
    const res = await api.restore({
      jobId: jobId || undefined,
      originalImage: {
        base64Data: restoreSource.base64Data,
        mimeType: restoreSource.mimeType,
        fileName: restoreSource.fileName,
      },
      analysis,
      instructionOverride,
      referenceImage: referenceImage
        ? {
            base64Data: referenceImage.base64Data,
            mimeType: referenceImage.mimeType,
            fileName: referenceImage.fileName,
          }
        : undefined,
      damageMask: damageMask
        ? {
            base64Data: damageMask.base64Data,
            mimeType: damageMask.mimeType,
            fileName: damageMask.fileName,
          }
        : undefined,
      maskSummary,
      isTweak: Boolean(options?.isTweak),
    });

    if (res.jobId) jobIdsByKey.set(key, res.jobId);
    if (!res.imageUrl || typeof res.imageUrl !== 'string') {
      throw new KinveraApiError('Restoration returned no image data from the API.', {
        code: 'RESTORE_EMPTY',
        retryable: true,
      });
    }
    return {
      imageUrl: res.imageUrl,
      commentary: res.commentary,
      jobId: res.jobId,
      warning: res.warning,
    };
  } catch (error: unknown) {
    if (error instanceof KinveraApiError) {
      if (error.status === 402 || error.code === 'INSUFFICIENT_CREDITS') {
        throw new KinveraApiError(
          error.message ||
            'This restoration requires 1 Kinvera credit. You currently do not have enough credits to begin the restoration.',
          error
        );
      }
      throw error;
    }
    const err = error as Error;
    throw new KinveraApiError(err.message || String(error), { code: 'RESTORE_FAILED', retryable: true });
  }
};
